//@flow
import React, {useEffect, useState} from 'react';
import {Link, useHistory} from 'react-router-dom';
import {useParams} from 'react-router-dom';
import {
  makeStyles,
  Typography,
  CircularProgress,
  IconButton,
} from '@material-ui/core';
import {colors} from '../../../../../../../assets/styles/colors';

import BannerMedia from '../BannerMedia';
import BannerLayout from '../BannerLayout';
import BannerMainButton from '../BannerMainButton';
import BannerTitles from '../BannerTitles';

import {FormProvider, useForm} from 'react-hook-form';
import CustomButton from '../../../../../../../elements/CustomButton/CustomButton';
import BannerEditor from '../BannerEditor';

import {
  BANNER_MARGIN_OPTIONS,
  BANNER_SHAPE_OPTIONS,
  MAIN_BANNER_FIELDS,
} from '../constants';
import {
  useBannerGroupActions,
  useCurrentBannerGroup,
} from '../../../../../utils/banner-group';
import {useStoreColorSettings} from '../../../../../../ui/utils';
import ConfirmModal from '../../../../../../../elements/Modals/ConfirmModal';
import {ArrowBack, ErrorOutline, HelpOutline} from '@material-ui/icons';
import CustomModal from '../../../../../../../elements/CustomModal/CustomModal';
import {BANNER_LAYOUTS} from '../../../../../constants';
import BannerSize from '../BannerSize';
import BannerMarginOption from '../BannerMarginOption';
import BannerBehaviorSection from '../BannerBehaviorSection';

import type {CreateBannerParams} from '../../../../../utils/banner-group';
import Collapsible from '../../../../../../../elements/Collapsible/Collapsible';
import {useAlert} from '../../../../../../../elements/Alert/AlertContext';
import {useHomePageLayoutActions} from '../../../../../../page-layout/utils/home-page';
import {NEW_BANNER_GROUP_ID} from '../../../../../utils/home-page-sections/constants';

type Props = {};

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    height: '100%',
    maxWidth: '1200px',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    width: '100%',
    height: '100%',
  },
  title: {
    fontSize: '1rem',
    fontWeight: '600',
    color: colors.grey900,
  },
  subtitle: {
    fontSize: '0.875rem',
    fontWeight: '400',
    color: colors.grey600,
  },
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '1rem',

    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: '70% 30%',
    },
  },
  collapse: {
    margin: '0 !important',
  },
  leftContent: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    gap: '1rem',
    position: 'relative',
    padding: '1rem',
    borderRadius: '12px',
    background: 'white',
    boxShadow:
      '0px 0.5px 2px 0px rgba(96, 97, 112, 0.16), 0px 0px 1px 0px rgba(40, 41, 61, 0.08);',
    overflow: 'auto',
  },
  rightContent: {
    display: 'none',
    [theme.breakpoints.up('lg')]: {
      height: 'min-content',
      position: 'sticky',
      top: '5.5rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      width: '100%',
    },
  },
  backButton: {
    background: 'white',
    padding: '4px',
    margin: '0 16px 0 0',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  rightImageSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  loader: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff80',
    cursor: 'progress',
    zIndex: 10,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',

    padding: '.75rem 0',
  },
  groupOptionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },

  sectionTitle: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: colors.grey900,
  },
  formSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  submitButton: {
    margin: '0',
    maxWidth: '240px',
    fontSize: '1.125rem',
    fontWeight: 600,
  },
  actions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '1rem',
  },
  modalClass: {
    width: '90vw',
    maxWidth: '350px',
    boxShadow: 'unset',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '.75rem',
  },
  warningIcon: {
    fontSize: '1.25rem',
    width: 100,
    height: 100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    background: colors.warning,
    color: 'white',
  },
  modalMessage: {
    fontSize: '0.875rem',
    color: colors.grey900,
  },
}));

export default function BannerDesignerForm(props: Props) {
  const classes = useStyles();

  const {bannerGroupId} = useParams();

  const colorConfig = useStoreColorSettings();

  const {showAlert} = useAlert();

  const {currentBannerGroup} = useCurrentBannerGroup();

  const bannerGroup = currentBannerGroup?.banners[0];

  const methods = useForm({
    defaultValues: {
      [MAIN_BANNER_FIELDS.CONTENT_POSITION]: BANNER_LAYOUTS.LEFT,
      [MAIN_BANNER_FIELDS.BANNER_SIZE]:
        bannerGroup?.bannerShapeType || BANNER_SHAPE_OPTIONS.FLAT,
      [MAIN_BANNER_FIELDS.BANNER_MARGIN]: bannerGroup?.padded
        ? BANNER_MARGIN_OPTIONS.WITH_MARGIN
        : BANNER_MARGIN_OPTIONS.WITHOUT_MARGIN,
      [MAIN_BANNER_FIELDS.TEXT_COLOR]:
        colorConfig && colorConfig.primaryFontColor,
      [MAIN_BANNER_FIELDS.BACKGROUND_COLOR]:
        colorConfig && colorConfig.primaryBackgroundColor,
      [MAIN_BANNER_FIELDS.BUTTON_BACKGROUND_COLOR]:
        colorConfig && colorConfig.secondaryFontColor,
      [MAIN_BANNER_FIELDS.BUTTON_TEXT_COLOR]:
        colorConfig && colorConfig.secondaryBackgroundColor,
    },
  });

  const history = useHistory();

  const {addSection} = useHomePageLayoutActions();

  const {createBanner, loading} = useBannerGroupActions();
  const [generatingBanner, setGeneratingBanner] = useState(false);

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const openConfirmModal = () => setConfirmModalOpen(true);
  const closeConfirmModal = () => setConfirmModalOpen(false);

  const [browserAlertOpen, setBrowserAlertOpen] = useState(false);

  const {register, setValue} = methods;

  useEffect(() => {
    register(MAIN_BANNER_FIELDS.CONTENT_POSITION);
    register(MAIN_BANNER_FIELDS.BANNER_SIZE);
    register(MAIN_BANNER_FIELDS.BANNER_MARGIN);
    register(MAIN_BANNER_FIELDS.IMAGE_DESKTOP);
    register(MAIN_BANNER_FIELDS.IMAGE_MOBILE);
    register(MAIN_BANNER_FIELDS.BUTTON_URL_IDENTIFIER, {
      required: 'Este campo es requerido',
    });
    register(MAIN_BANNER_FIELDS.BUTTON_URL_TYPE);

    register(MAIN_BANNER_FIELDS.BACKGROUND_COLOR);
    register(MAIN_BANNER_FIELDS.TEXT_COLOR);
    register(MAIN_BANNER_FIELDS.BUTTON_BACKGROUND_COLOR);
    register(MAIN_BANNER_FIELDS.BUTTON_TEXT_COLOR);
    register(MAIN_BANNER_FIELDS.VIDEO_URL);
    register(MAIN_BANNER_FIELDS.VIDEO_ID);
  }, [register]);

  useEffect(() => {
    if (!currentBannerGroup) return;
    const bannerGroup = currentBannerGroup?.banners[0];

    setValue(MAIN_BANNER_FIELDS.BANNER_SIZE, bannerGroup?.bannerShapeType);

    setValue(
      MAIN_BANNER_FIELDS.BANNER_MARGIN,
      bannerGroup?.padded
        ? BANNER_MARGIN_OPTIONS.WITH_MARGIN
        : BANNER_MARGIN_OPTIONS.WITHOUT_MARGIN
    );
  }, [currentBannerGroup, setValue]);

  useEffect(() => {
    const isChrome = navigator.userAgent.indexOf('Chrome') !== -1;

    if (isChrome) return;
    const isSafari = navigator.userAgent.indexOf('Safari') !== -1;

    setBrowserAlertOpen(isSafari);
  }, []);

  const onSubmit = async data => {
    setGeneratingBanner(true);

    console.log({data});
    try {
      const bannerPayload: CreateBannerParams = {
        contentPosition: data.contentPosition,

        buttonUrl: {
          identifier: data.buttonUrlIdentifier || undefined,
          type: data.buttonUrlType || undefined,
        },
        title: data.title,
        subtitle: data.subtitle,
        backgroundColor: data.backgroundColor,
        buttonBackgroundColor: data.buttonBackgroundColor,
        buttonTextColor: data.buttonTextColor,
        textColor: data.textColor,
        buttonText: data.buttonText,
        videoUrl: data.videoUrl,
        padded:
          data[MAIN_BANNER_FIELDS.BANNER_MARGIN] ===
          BANNER_MARGIN_OPTIONS.WITH_MARGIN,
        bannerShapeType: data[MAIN_BANNER_FIELDS.BANNER_SIZE],
      };

      const bannerType = data.videoUrl ? 'video' : 'image';

      if (bannerType === 'image') {
        const desktopImage = data[MAIN_BANNER_FIELDS.IMAGE_DESKTOP];
        const mobileImage = data[MAIN_BANNER_FIELDS.IMAGE_MOBILE];

        if (!desktopImage || !mobileImage) {
          showAlert('Debes subir las imágenes o el link del video', {
            type: 'error',
            duration: 5000,
          });
          return;
        }

        bannerPayload.images = {
          desktop: desktopImage,
          mobile: mobileImage,
        };
      }
      const bannerGroup = await createBanner(bannerPayload);

      if (bannerGroupId === NEW_BANNER_GROUP_ID) {
        addSection({
          enabled: true,
          pageSectionType: 'STORE_BANNER',
          sectionIdentifier: bannerGroup.id,
        });
      }

      history.push(`/my-store/pages/home-page#${bannerGroup.id}`);
    } catch (error) {
      console.log(error);
    } finally {
      setGeneratingBanner(false);
    }
  };

  const handleSubmit = (event: Event) => {
    event.preventDefault();

    openConfirmModal();
  };

  const isLoading = loading || generatingBanner;

  return (
    <div className={classes.root}>
      <header className={classes.header}>
        <IconButton
          className={classes.backButton}
          component={Link}
          to="/my-store/pages/home-page"
        >
          <ArrowBack />
        </IconButton>
        <Typography className={classes.title}>Crea un banner</Typography>
      </header>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit} className={classes.formContainer}>
          {isLoading && (
            <div className={classes.loader}>
              <CircularProgress size={34} color="primary" />
            </div>
          )}
          <div className={classes.container}>
            <Collapsible
              title="Configuraciones del grupo de Banners"
              className={classes.collapse}
              divisible={false}
            >
              <div className={classes.groupOptionContainer}>
                <Typography
                  className={classes.subtitle}
                  style={{
                    marginTop: '-1.25rem',
                  }}
                >
                  Cualquier cambio aquí afectará a todos los banners en este
                  grupo.
                </Typography>
                <section className={classes.formSection}>
                  <Typography className={classes.sectionTitle}>
                    Selecciona la dimensión
                  </Typography>
                  <BannerSize />
                </section>

                <section className={classes.formSection}>
                  <Typography className={classes.sectionTitle}>
                    Opciones de Márgenes
                  </Typography>
                  <BannerMarginOption />
                </section>
              </div>
            </Collapsible>
          </div>

          <div className={classes.container}>
            <div className={classes.leftContent}>
              <Typography className={classes.title}>
                Configuraciones del Banner
              </Typography>
              <section className={classes.formSection}>
                <Typography className={classes.sectionTitle}>
                  1. Selecciona una estructura
                </Typography>
                <BannerLayout />
              </section>

              <section className={classes.formSection}>
                <Typography className={classes.sectionTitle}>
                  2. Sube una imagen o video
                </Typography>

                <BannerMedia />
              </section>

              <section className={classes.formSection}>
                <Typography className={classes.sectionTitle}>
                  3. Define el título, subtítulo y el color del fondo y texto
                  (opcional)
                </Typography>
                <BannerTitles />
              </section>

              <section className={classes.formSection}>
                <Typography className={classes.sectionTitle}>
                  4.Configura tu botón y elige el color del fondo y texto
                  (opcional)
                </Typography>
                <BannerMainButton />
              </section>
              <section className={classes.formSection}>
                <Typography className={classes.sectionTitle}>
                  5. Selecciona un comportamiento
                </Typography>
                <BannerBehaviorSection />
              </section>
              <section className={classes.formSection}>
                <Typography className={classes.sectionTitle}>
                  6.Acomoda la imagen del banner
                </Typography>
                <BannerEditor />
              </section>

              <footer className={classes.actions}>
                <CustomButton
                  className={classes.submitButton}
                  color="primary"
                  variant="contained"
                  fullWidth
                  type="submit"
                >
                  Guardar
                </CustomButton>
              </footer>
            </div>
          </div>
        </form>
      </FormProvider>

      <ConfirmModal
        open={confirmModalOpen}
        title="Antes de guardar el banner"
        message="Asegúrate de estar satisfecho con el resultado, ya que no podrás modificarlo una vez guardado."
        onCancel={closeConfirmModal}
        onClose={closeConfirmModal}
        icon={<HelpOutline fontSize="large" />}
        onConfirm={() => {
          methods.handleSubmit(onSubmit)();
          closeConfirmModal();
        }}
      />

      <CustomModal
        open={browserAlertOpen}
        onClose={() => setBrowserAlertOpen(false)}
      >
        <div className={classes.modalClass}>
          <span className={classes.warningIcon}>
            <ErrorOutline fontSize="large" />
          </span>
          <Typography className={classes.modalMessage}>
            Hemos detectado que estás utilizando <strong>Safari</strong>,
            algunas versiones de este navegador no son compatibles con esta
            herramienta.
          </Typography>
          <Typography className={classes.modalMessage}>
            Es posible que experimentes problemas de rendimiento o funcionalidad
            en esta página. Recomendamos usar &nbsp;
            <strong>Google Chrome</strong>&nbsp; para una experiencia óptima.
          </Typography>

          <CustomButton
            variant="contained"
            onClick={() => setBrowserAlertOpen(false)}
          >
            Continuar
          </CustomButton>
        </div>
      </CustomModal>
    </div>
  );
}
